import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader } from "lucide-react";
import axios from "../../config/axios";

/**
 * OAuthCallback Component
 * Handles OAuth callback from Airbyte/connector providers
 */
const OAuthCallback = () => {
  const { instanceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [message, setMessage] = useState("Processing authentication...");
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate calls (React Strict Mode calls useEffect twice in dev)
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;
    handleOAuthCallback();
  }, []);

  const handleOAuthCallback = async () => {
    try {
      // Get parameters from URL
      const secretId = searchParams.get("secret_id");
      const error = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      console.log("OAuth callback params:", {
        secretId,
        error,
        errorDescription,
        allParams: Object.fromEntries(searchParams.entries()),
      });

      // Check for OAuth errors
      if (error) {
        setStatus("error");
        setMessage(errorDescription || "Authentication failed");

        // Notify parent window if opened as popup
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "oauth-error",
              error: errorDescription || error,
            },
            window.location.origin,
          );

          setTimeout(() => {
            window.close();
          }, 3000);
        }
        return;
      }

      // Validate required parameters
      if (!secretId) {
        throw new Error("Missing OAuth secret_id parameter");
      }

      if (!instanceId) {
        throw new Error("Missing instance ID parameter");
      }

      // Complete OAuth flow with backend
      const response = await axios.post(
        `/api/connectors/oauth/callback/${instanceId}`,
        {
          secretId,
        },
      );

      if (response.data.success) {
        setStatus("success");
        setMessage("Authentication successful!");

        // Notify parent window if opened as popup
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "oauth-success",
              sourceId: response.data.sourceId,
              connectorId: response.data.connectorId,
            },
            window.location.origin,
          );

          setTimeout(() => {
            window.close();
          }, 2000);
        } else {
          // Navigate to connector detail page to monitor setup progress
          setTimeout(() => {
            navigate(`/datasources/${instanceId}`);
          }, 2000);
        }
      } else {
        throw new Error(response.data.error || "Authentication failed");
      }
    } catch (error) {
      console.error("OAuth callback error:", error);
      setStatus("error");
      setMessage(
        error.response?.data?.error?.message ||
        error.message ||
        "Authentication failed",
      );

      // Notify parent window if opened as popup
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "oauth-error",
            error: error.message,
          },
          window.location.origin,
        );

        setTimeout(() => {
          window.close();
        }, 3000);
      }
    }
  };

  const renderContent = () => {
    switch (status) {
      case "processing":
        return (
          <div className="flex flex-col items-center">
            <Loader className="w-16 h-16 text-primary-600 animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Processing Authentication
            </h2>
            <p className="text-gray-600 text-center max-w-md">{message}</p>
          </div>
        );

      case "success":
        return (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Authentication Successful
            </h2>
            <p className="text-gray-600 text-center max-w-md">{message}</p>
            <p className="text-sm text-gray-500 mt-4">
              {window.opener
                ? "This window will close automatically..."
                : "Redirecting..."}
            </p>
          </div>
        );

      case "error":
        return (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Authentication Failed
            </h2>
            <p className="text-gray-600 text-center max-w-md">{message}</p>
            <div className="mt-6 space-y-3">
              <button
                onClick={() => window.close()}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close Window
              </button>
              {!window.opener && (
                <button
                  onClick={() => navigate("/connectors")}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 ml-3"
                >
                  Back to Connectors
                </button>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        {renderContent()}
      </div>
    </div>
  );
};

export default OAuthCallback;
